import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from 'recharts';

export default function Analytics() {
  const [data, setData] = useState(null);
  const [top, setTop] = useState([]);
  const [attribution, setAttribution] = useState(null);
  useEffect(() => {
    api.get('/analytics/overview').then(({ data }) => setData(data));
    api.get('/analytics/top-products').then(({ data }) => setTop(data.items));
    api.get('/seller/revenue-attribution')
      .then(({ data }) => setAttribution(data))
      .catch(() => setAttribution(null)); // 403 (non Pro/Enterprise) → hide section
  }, []);
  if (!data) return <div className="text-fab-text-muted">Loading analytics...</div>;
  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

  return (
    <div className="space-y-5 fade-up">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="fab-card p-4"><div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Revenue</div><div className="font-display text-2xl font-bold text-fab-text">{fmt(data.revenue)}</div></div>
        <div className="fab-card p-4"><div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Orders</div><div className="font-display text-2xl font-bold text-fab-text">{data.total_orders}</div></div>
        <div className="fab-card p-4"><div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Customers</div><div className="font-display text-2xl font-bold text-fab-text">{data.customers_count}</div></div>
        <div className="fab-card p-4"><div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Hot Leads</div><div className="font-display text-2xl font-bold text-fab-orange">{data.hot_leads}</div></div>
      </div>

      {attribution && (
        <div data-testid="revenue-attribution-section">
          <div className="font-display font-bold text-fab-text mb-2">Revenue Attribution</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="fab-card p-4" data-testid="attr-revenue-card">
              <div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Attributed Revenue</div>
              <div className="font-display text-2xl font-bold text-fab-text" data-testid="attr-revenue-value">{fmt(attribution.attributed_revenue)}</div>
            </div>
            <div className="fab-card p-4" data-testid="attr-orders-card">
              <div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Attributed Orders</div>
              <div className="font-display text-2xl font-bold text-fab-text" data-testid="attr-orders-value">{attribution.attributed_order_count}</div>
            </div>
            <div className="fab-card p-4" data-testid="attr-unavailable-card">
              <div className="text-[11px] text-fab-text-muted uppercase tracking-wider">Attribution Unavailable</div>
              <div className="font-display text-2xl font-bold text-fab-text-muted" data-testid="attr-unavailable-value">{attribution.attribution_unavailable}</div>
            </div>
          </div>
        </div>
      )}

      {attribution && attribution.categories && (
        <div className="space-y-3" data-testid="attr-breakdown-section">
          <div className="font-display text-fab-text font-bold">
            Revenue Attribution Breakdown
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ["unclear", "Unclear"],
              ["follow_ups", "Follow-ups"],
              ["objection_handling", "Orders preceded by a recorded objection"],
              ["order_flow", "Order Flow"],
            ].map(([key, label]) => (
              <div className="fab-card p-4" key={key} data-testid={`attr-category-${key}`}>
                <div className="text-[11px] text-fab-text-muted uppercase tracking-wide">
                  {label}
                </div>
                <div className="font-display text-xl font-bold text-fab-text">
                  {fmt(attribution.categories[key]?.revenue)}
                </div>
                <div className="text-sm text-fab-text-muted">
                  {attribution.categories[key]?.order_count ?? 0} orders
                </div>
              </div>
            ))}
          </div>
          {attribution.reconciliation && (
            <div className="fab-card p-4" data-testid="attr-reconciliation">
              <div className="font-semibold text-fab-text mb-2">
                Revenue Reconciliation
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="text-sm text-fab-text-muted">Dashboard Revenue</div>
                  <div className="font-display font-bold">
                    {fmt(attribution.reconciliation.dashboard_revenue)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-fab-text-muted">Categorized Revenue</div>
                  <div className="font-display font-bold">
                    {fmt(attribution.reconciliation.categorized_revenue)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-fab-text-muted">Difference</div>
                  <div className="font-display font-bold">
                    {fmt(attribution.reconciliation.difference)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="fab-card p-5">
          <div className="font-display font-bold text-fab-text mb-3">Revenue Trend (30 days)</div>
          <div className="h-[260px]">
            <ResponsiveContainer><LineChart data={data.timeline}>
              <CartesianGrid stroke="#EAE8FF" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9191A8' }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: '#9191A8' }} width={50} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #EAE8FF', fontSize: 12 }} />
              <Line type="monotone" dataKey="revenue" stroke="#6C5CE7" strokeWidth={2.5} dot={false} />
            </LineChart></ResponsiveContainer>
          </div>
        </div>
        <div className="fab-card p-5">
          <div className="font-display font-bold text-fab-text mb-3">Orders Volume</div>
          <div className="h-[260px]">
            <ResponsiveContainer><BarChart data={data.timeline}>
              <CartesianGrid stroke="#EAE8FF" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9191A8' }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: '#9191A8' }} width={30} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #EAE8FF', fontSize: 12 }} />
              <Bar dataKey="orders" fill="#25D366" radius={[6, 6, 0, 0]} />
            </BarChart></ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="fab-card p-5">
        <div className="font-display font-bold text-fab-text mb-3">Top Products by Revenue</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {top.map((p, i) => (
            <div key={i} className="text-center">
              {p.image && <img src={p.image} className="w-full aspect-square object-cover rounded-lg mb-2" alt={p.code} />}
              <div className="font-mono text-[11px] font-bold">{p.code}</div>
              <div className="text-[11px] text-fab-text-muted">{p.qty} sold</div>
              <div className="text-[12px] font-bold text-fab-primary">{fmt(p.revenue)}</div>
            </div>
          ))}
          {top.length === 0 && <div className="col-span-full text-center text-fab-text-muted text-[13px] py-6">Not enough order data yet.</div>}
        </div>
      </div>
    </div>
  );
}
